import queryString from 'query-string';
import {
  Alert,
  AlertsInput,
  AnalysisPack,
  AnalysisPackSource,
  APIToken,
  CloudAccount,
  CorrelationRule,
  DataModel,
  Destination,
  DestinationTypeEnum,
  DetectionType,
  GlobalHelper,
  ListAnalysisPacksInput,
  ListDetectionsInput,
  ListDetectionsSortFieldsEnum,
  LogIntegration,
  LogPullerTypeEnum,
  Lookup,
  Policy,
  Replay,
  ResourceDetails,
  Role,
  Rule,
  Schema,
  SortDirEnum,
} from 'Generated/schema';
import { QueryBuilderUrlParams } from 'Pages/Investigate/QueryBuilder/types';
import {
  CustomEnrichmentMethodEnum,
  EnrichmentSourcesEnum,
  ListLogSourceFilterValues,
  ListSchemasURLParams,
  LogSourceType,
} from 'Source/constants';
import { ListDataSchemasSortFieldsEnum } from 'Source/ListDataSchemasSortFieldsEnum';
import { ListLogSourceSortFieldsEnum } from 'Source/ListLogSourceSortFieldsEnum';
import {
  QueryBuilderRelayAlertEventsParams,
  QueryBuilderRelayDashboardWidgetParams,
  QueryBuilderRelayKeyValueParams,
  QueryBuilderRelayLogTypeParams,
} from 'Pages/Investigate/QueryBuilderRelay';

import { APIKind } from 'Pages/Settings/APIToken/APIPlayground';

export interface CreateDetectionOpts {
  sessionId?: string;
  mitre?: { tacticId: string; techniqueId: string };
  relatedDetectionId?: string;
  relationOperation?: 'clone' | 'inherit';
  type?: DetectionType;
  correlationRuleRefs?: string[];
  version?: 'v1' | 'v2';
  isSimpleDetection?: boolean;
}

// Typical URL encoding, allowing colons (:) to be present in the URL. Colons are safe.
// https://stackoverflow.com/questions/14872629/uriencode-and-colon
export const urlEncode = (str: string) => encodeURIComponent(str).replace(/%3A/g, unescape);

const urls = {
  dashboard: {
    home: () => '/dashboard/',
    overview: () => `${urls.dashboard.home()}overview/`,
    cloudSecurity: () => `${urls.dashboard.home()}cloud-security/`,
    data: () => `${urls.dashboard.home()}data/`,
    mitreMatrix: (opts?: { matrixId?: string; tacticId?: string; techniqueId?: string }) => {
      const base = `${urls.dashboard.home()}mitre-attack/`;
      const qs = queryString.stringify({
        matrix: opts?.matrixId,
        tactic: opts?.tacticId,
        technique: opts?.techniqueId,
      });
      return qs ? `${base}?${qs}` : base;
    },
    mitreMapping: ({ tacticId, techniqueId }: { tacticId?: string; techniqueId?: string }) => {
      const base = `${urls.dashboard.mitreMatrix()}edit-mapping/`;
      const qs = queryString.stringify({ tactic: tacticId, technique: techniqueId });
      return qs ? `${base}?${qs}` : base;
    },
    customDashboard: (id: string) => `${urls.dashboard.home()}${id}/`,
  },
  investigate: {
    home: () => '/investigate/',
    search: (params?: QueryBuilderUrlParams) => {
      let url = `${urls.investigate.home()}search/`;
      if (params) {
        url = `${url}?${queryString.stringify(params)}`;
      }

      return url;
    },
    searchRelay: {
      alertEvents: (params?: QueryBuilderRelayAlertEventsParams) => {
        let url = `${urls.investigate.home()}alert-events-search/`;
        if (params) {
          url = `${url}?${queryString.stringify(params, { arrayFormat: 'bracket' })}`;
        }

        return url;
      },
      keyValue: (params?: QueryBuilderRelayKeyValueParams) => {
        let url = `${urls.investigate.home()}key-value-search/`;
        if (params) {
          url = `${url}?${queryString.stringify(params)}`;
        }

        return url;
      },
      logType: (params?: QueryBuilderRelayLogTypeParams) => {
        let url = `${urls.investigate.home()}log-type-search/`;
        if (params) {
          url = `${url}?${queryString.stringify(params)}`;
        }

        return url;
      },
      dashboardWidget: (params?: QueryBuilderRelayDashboardWidgetParams) => {
        let url = `${urls.investigate.home()}dashboard-widget-search/`;
        if (params) {
          url = `${url}?${queryString.stringify(params, { arrayFormat: 'bracket' })}`;
        }

        return url;
      },
    },
    dataExplorer: () => `${urls.investigate.home()}data-explorer/`,
    savedSearches: {
      home: () => `${urls.investigate.home()}saved-searches/`,
      list: () => urls.investigate.savedSearches.home(),
    },
    aiResponseSearch: (params?: { query?: string; searchAllResponses?: boolean }) => {
      let url = `${urls.investigate.home()}ai-response-search/`;
      if (params) {
        url = `${url}?${queryString.stringify(params)}`;
      }
      return url;
    },
    searchHistory: () => `${urls.investigate.home()}search-history/`,
    cloudResources: {
      home: () => `${urls.investigate.home()}resources/`,
      list: () => urls.investigate.cloudResources.home(),
      details: (id: ResourceDetails['id']) =>
        `${urls.investigate.cloudResources.list()}${urlEncode(id)}/`,
    },
  },
  runbooks: {
    home: () => '/runbooks/',
  },
  build: {
    home: () => '/build/',
    dataModels: {
      list: () => `${urls.build.home()}data-models/`,
      create: () => `${urls.build.dataModels.list()}new/`,
      details: (id: DataModel['id']) => `${urls.build.dataModels.list()}${urlEncode(id)}/`,
      edit: (id: DataModel['id']) => `${urls.build.dataModels.details(id)}edit/`,
    },
    detections: {
      home: () => `${urls.build.home()}detections/`,
      list: ({
        disableDefaultParams = true,
        params = {},
      }: { disableDefaultParams?: boolean; params?: ListDetectionsInput } = {}) => {
        const defaultParams = {
          page: 1,
          sortBy: ListDetectionsSortFieldsEnum.LastModified,
          sortDir: SortDirEnum.Descending,
        };
        const urlParams = disableDefaultParams ? { ...params } : { ...defaultParams, ...params };

        if (Object.keys(urlParams).length === 0) {
          return urls.build.detections.home();
        }

        return `${urls.build.detections.home()}?${queryString.stringify(urlParams, {
          arrayFormat: 'bracket',
        })}`;
      },
      create: (opts?: CreateDetectionOpts) => {
        const base = `${urls.build.detections.home()}new/`;
        const qs = queryString.stringify(
          {
            sessionId: opts?.sessionId,
            mitreTactic: opts?.mitre?.tacticId,
            mitreTechnique: opts?.mitre?.techniqueId,
            relatedDetectionId: opts?.relatedDetectionId,
            relationOperation: opts?.relationOperation,
            type: opts?.type,
            correlationRuleRefs: opts?.correlationRuleRefs,
            isSimpleDetection: opts?.isSimpleDetection,
            version: opts?.version,
          },
          { arrayFormat: 'comma' },
        );

        return qs ? `${base}?${qs}` : base;
      },
      details: (id: string, type: DetectionType) => {
        switch (type) {
          case DetectionType.Policy:
            return urls.build.detections.policies.details(id);
          case DetectionType.Rule:
          case DetectionType.ScheduledRule:
            return urls.build.detections.rules.details(id);
          case DetectionType.CorrelationRule:
            return urls.build.detections.correlationRules.details(id);
          default:
            return urls.build.detections.list();
        }
      },
      rules: {
        list: () => `${urls.build.detections.list()}rules/`,
        create: (opts?: CreateDetectionOpts) =>
          urls.build.detections.create({ type: DetectionType.Rule, ...(opts || {}) }),
        details: (id: Rule['id']) => `${urls.build.detections.rules.list()}${urlEncode(id)}/`,
        edit: (id: Rule['id']) => `${urls.build.detections.rules.details(id)}edit/`,
        replays: (detectionId: Rule['id'], replayId: Replay['id'], alertId: Alert['id']) =>
          `${urls.build.detections.rules.list()}${detectionId}/replay/${urlEncode(
            replayId,
          )}/alert/${alertId}`,
      },
      policies: {
        list: () => `${urls.build.detections.list()}policies/`,
        create: (opts?: CreateDetectionOpts) =>
          urls.build.detections.create({ type: DetectionType.Policy, ...(opts || {}) }),
        details: (id: Policy['id']) => `${urls.build.detections.policies.list()}${urlEncode(id)}/`,
        edit: (id: Policy['id']) => `${urls.build.detections.policies.details(id)}edit/`,
      },
      correlationRules: {
        list: () => `${urls.build.detections.list()}correlation-rules/`,
        details: (id: CorrelationRule['id']) =>
          `${urls.build.detections.correlationRules.list()}${urlEncode(id)}/`,
        edit: (id: CorrelationRule['id']) =>
          `${urls.build.detections.correlationRules.details(id)}edit/`,
      },
    },
    globalHelpers: {
      home: () => `${urls.build.home()}helpers/`,
      list: () => urls.build.globalHelpers.home(),
      create: () => `${urls.build.globalHelpers.list()}new/`,
      edit: (id: GlobalHelper['id']) => `${urls.build.globalHelpers.list()}${urlEncode(id)}/edit/`,
    },
    packs: {
      home: () => `${urls.build.home()}packs/`,
      list: ({ disableDefaultParams = true } = {}) => {
        if (disableDefaultParams) {
          return urls.build.packs.home();
        }
        const params: ListAnalysisPacksInput & { section: string } = {
          sortDir: SortDirEnum.Ascending,
          sortBy: 'enabled',
          section: 'packs',
        };
        return `${urls.build.packs.home()}?${queryString.stringify(params)}`;
      },
      details: (id: AnalysisPack['id']) => `${urls.build.packs.home()}${urlEncode(id)}/`,
      packSources: {
        home: () => `${urls.build.home()}packs/sources/`,
        list: () => `${urls.build.packs.home()}?section=pack-sources`,
        create: () => `${urls.build.packs.packSources.home()}new-detection-pack-source/`,
        details: () => `${urls.build.packs.packSources.home()}edit-pack-source/`,
        edit: (id: AnalysisPackSource['id']) =>
          `${urls.build.packs.packSources.details()}${urlEncode(id)}/`,
      },
    },
  },
  configure: {
    home: () => '/configure/',
    alertDestinations: {
      home: () => `${urls.configure.home()}destinations/`,
      list: () => urls.configure.alertDestinations.home(),
      create: (name?: DestinationTypeEnum | ':name') =>
        `${urls.configure.alertDestinations.list()}new/${name ?? ''}`,
      edit: (id: Destination['outputId']) =>
        `${urls.configure.alertDestinations.list()}${urlEncode(id)}/edit/`,
    },
    cloudAccounts: {
      home: () => `${urls.configure.home()}cloud-accounts/`,
      list: () => urls.configure.cloudAccounts.home(),
      create: () => `${urls.configure.cloudAccounts.list()}new/`,
      edit: (id: CloudAccount['id']) => `${urls.configure.cloudAccounts.list()}${id}/edit/`,
    },
    enrichmentProviders: {
      home: () => `${urls.configure.home()}enrichments/`,
      list: () => urls.configure.enrichmentProviders.home(),
      details: (id: Lookup['id']) => `${urls.configure.enrichmentProviders.home()}${id}/`,
      create: (type?: EnrichmentSourcesEnum | CustomEnrichmentMethodEnum) =>
        `${urls.configure.enrichmentProviders.home()}new/${type ? `${type}/` : ''}`,
      edit: (id: Lookup['id']) => `${urls.configure.enrichmentProviders.details(id)}edit/`,
    },
    logSources: {
      home: () => `${urls.configure.home()}log-sources/`,
      details: (id: LogIntegration['integrationId']) => `${urls.configure.logSources.list()}${id}/`,
      list: ({
        disableDefaultParams = true,
        params = {},
      }: { disableDefaultParams?: boolean; params?: ListLogSourceFilterValues } = {}) => {
        const defaultParams = {
          sortBy: ListLogSourceSortFieldsEnum.dateCreated,
          sortDir: SortDirEnum.Descending,
        };
        const urlParams = disableDefaultParams ? { ...params } : { ...defaultParams, ...params };

        if (Object.keys(urlParams).length === 0) {
          return urls.configure.logSources.home();
        }

        return `${urls.configure.logSources.home()}?${queryString.stringify(urlParams, {
          arrayFormat: 'bracket',
        })}`;
      },
      create: (type?: LogSourceType) =>
        `${urls.configure.logSources.list()}add-new-source/${type ? `${type}/` : ''}`,
      edit: (id: LogIntegration['integrationId']) =>
        `${urls.configure.logSources.details(id)}edit/`,
      schemasManagement: (id: LogIntegration['integrationId']) => ({
        home: () => `${urls.configure.logSources.details(id)}schemas/`,
        draftSchema: (schemaName: string) =>
          `${urls.configure.logSources.schemasManagement(id).home()}draft-schema/${schemaName}`,
        testReport: (reportId: string) =>
          `${urls.configure.logSources.schemasManagement(id).home()}test-report/${reportId}`,
      }),
      authorize: (id: LogIntegration['integrationId'], type: LogPullerTypeEnum) =>
        `${urls.configure.logSources.list()}${type}/${id}/authorize/`,
    },
    schemas: {
      home: () => `${urls.configure.home()}schemas/`,
      list: ({ disableDefaultParams = true } = {}) => {
        if (disableDefaultParams) {
          return urls.configure.schemas.home();
        }
        const defaultParams: ListSchemasURLParams = {
          isArchived: false,
          sortBy: ListDataSchemasSortFieldsEnum.name,
          sortDir: SortDirEnum.Ascending,
        };

        return `${urls.configure.schemas.home()}?${queryString.stringify(defaultParams)}`;
      },
      create: () => `${urls.configure.schemas.list()}new/`,
      details: (name: Schema['name']) => `${urls.configure.schemas.list()}${urlEncode(name)}/`,
      edit: (name: Schema['name']) => `${urls.configure.schemas.details(name)}edit/`,
    },
  },
  contentCatalog: {
    home: () => '/content-catalog/',
    browse: () => `${urls.contentCatalog.home()}browse/`,
    installed: () => `${urls.contentCatalog.home()}installed/`,
    workbench: (item?: { id: string; analysisType: string }) =>
      item
        ? `${urls.contentCatalog.home()}workbench/?item=${encodeURIComponent(
            item.id,
          )}&type=${encodeURIComponent(item.analysisType)}`
        : `${urls.contentCatalog.home()}workbench/`,
  },
  alerts: {
    home: () => '/alerts-and-errors/',
    list: ({
      disableDefaultParams = true,
      ...rest
    }: { disableDefaultParams?: boolean } & Partial<AlertsInput> & { tab?: string } = {}) => {
      if (disableDefaultParams) {
        return urls.alerts.home();
      }
      const params: Partial<AlertsInput> = {
        ...rest,
      };

      return `${urls.alerts.home()}?${queryString.stringify(params, { arrayFormat: 'bracket' })}`;
    },
    details: (id: Alert['id']) => `${urls.alerts.list()}${urlEncode(id)}/`,
  },
  ai: {
    home: () => '/ai/',
    history: {
      home: () => `${urls.ai.home()}history/`,
      bookmarked: () => `${urls.ai.history.home()}bookmarked/`,
      favoritePrompts: () => `${urls.ai.history.home()}favorite-prompts/`,
      archived: () => `${urls.ai.history.home()}archived/`,
    },
    scheduledPrompts: {
      list: () => `${urls.ai.home()}scheduled-prompts/`,
      details: (id: string) => `${urls.ai.scheduledPrompts.list()}${id}/`,
    },
  },
  settings: {
    home: () => `/settings/`,
    usage: () => `${urls.settings.home()}usage/`,
    general: {
      home: () => `${urls.settings.home()}general/`,
      mainInfo: () => `${urls.settings.general.home()}main-info/`,
      theme: () => `${urls.settings.general.home()}theme/`,
      aiPreferences: () => `${urls.settings.general.home()}ai-preferences/`,
      infraDashboard: () => `${urls.settings.general.home()}infra-dashboard/`,
    },
    about: {
      home: () => `${urls.settings.home()}about/`,
      licenses: () => `${urls.settings.about.home()}licenses/`,
    },
    auth: {
      home: () => `${urls.settings.home()}auth/`,
      password: () => `${urls.settings.auth.home()}password/`,
      identity: () => `${urls.settings.auth.home()}identity/`,
    },
    dataLake: {
      home: () => `${urls.settings.home()}data-lake/`,
      snowflake: () => `${urls.settings.dataLake.home()}snowflake/`,
      scheduledSearches: () => `${urls.settings.dataLake.home()}scheduled-searches/`,
    },
    developer: {
      home: () => `${urls.settings.home()}developer/`,
      gitSync: () => `${urls.settings.developer.home()}git-sync/`,
      analysisTool: () => `${urls.settings.developer.home()}analysis-tool/`,
    },
    roles: {
      list: () => `${urls.settings.home()}roles/`,
      create: () => `${urls.settings.roles.list()}new/`,
      details: (id: Role['id']) => `${urls.settings.roles.list()}${urlEncode(id)}/`,
      edit: (id: Role['id']) => `${urls.settings.roles.details(id)}edit/`,
    },
    users: () => `${urls.settings.home()}users/`,
    api: {
      home: () => `${urls.settings.home()}api/`,
      list: () => `${urls.settings.api.home()}tokens/`,
      create: () => `${urls.settings.api.list()}new/`,
      details: (id: APIToken['id']) => `${urls.settings.api.list()}${urlEncode(id)}/`,
      edit: (id: APIToken['id']) => `${urls.settings.api.details(id)}edit/`,
      playground: (kind?: APIKind | ':kind' | '') => {
        const base = `${urls.settings.api.home()}playground/`;
        if (kind) {
          return `${base}${kind}/`;
        }
        return base;
      },
    },
    ai: {
      home: () => `${urls.settings.home()}ai/`,
      configuration: () => `${urls.settings.ai.home()}configuration/`,
      alertTriage: () => `${urls.settings.ai.home()}alert-triage/`,
      webAccess: () => `${urls.settings.ai.home()}web-access/`,
      remoteMcp: () => `${urls.settings.ai.home()}remote-mcp/`,
    },
    profile: () => `${urls.settings.home()}profile/`,
    mcp: {
      list: () => `${urls.settings.ai.home()}mcp/`,
      callback: () => `${urls.settings.home()}mcp/callback`,
      tools: (serverId: string) => `${urls.settings.home()}mcp/${serverId}/tools/`,
    },
    alerts: {
      contextTags: () => `${urls.settings.home()}alerts/context-tags/`,
    },
    aiSkills: {
      list: () => `${urls.settings.ai.home()}skills/`,
      create: () => `${urls.settings.aiSkills.list()}new/`,
      details: (id: string) => `${urls.settings.aiSkills.list()}${urlEncode(id)}/`,
      edit: (id: string) => `${urls.settings.aiSkills.details(id)}edit/`,
    },
  },
  account: {
    auth: {
      signIn: () => `/sign-in/`,
      singleSignOn: () => `/oauth/`,
      forgotPassword: () => `/password-forgot/`,
      resetPassword: () => `/password-reset/`,
      // Remote-MCP OAuth consent screen. Users land here after signing
      // in via the mcp_auth=true handoff in GuardedRoute.
      mcpApproval: () => `/mcp-approval/`,
    },
    support: () => `/support/`,
  },
  public: {
    githubAppRegister: () => `/public/github-app/register`,
  },
};

export const getUrlWithDefaultParams = (url: string) => {
  switch (url) {
    case urls.configure.logSources.list():
      return urls.configure.logSources.list({ disableDefaultParams: false });
    case urls.configure.schemas.list():
      return urls.configure.schemas.list({ disableDefaultParams: false });
    case urls.alerts.list():
      return urls.alerts.list({ disableDefaultParams: false });
    case urls.build.detections.list():
      return urls.build.detections.list({ disableDefaultParams: false });
    case urls.build.packs.list():
      return urls.build.packs.list({ disableDefaultParams: false });
    default:
      return url;
  }
};

export default urls;
